import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { IconArrowHead, NamedLink, Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { withViewport } from '../../util/uiHelpers';
import { useConfiguration } from '../../context/configurationContext';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import css from './SellPage.module.css';

// Static assets
import facebookImage from '../../assets/tradmarket-facebook-sharing-1200x630.jpg';
import twitterImage from '../../assets/tradmarket-twitter-sharing-600x315.jpg';

// Hero image
import heroImage from '../../assets/sell/sell-hero.jpg';

// Feature images
import featureImage1 from '../../assets/sell/sell-feature-1.jpg';
import featureImage2 from '../../assets/sell/sell-feature-2.jpg';
import featureImage3 from '../../assets/sell/sell-feature-3.jpg';
import featureImage4 from '../../assets/sell/sell-feature-4.jpg';

// How It Works image
import howItWorksImage from '../../assets/sell/sell-how-it-works.jpg';

export const SellPageComponent = props => {
  const { intl, scrollingDisabled } = props;

  const config = useConfiguration();
  const marketplaceName = config.marketplaceName;

  const schemaTitle = intl.formatMessage({ id: 'SellPage.schemaTitle' }, { marketplaceName });
  const schemaDescription = intl.formatMessage({ id: 'SellPage.schemaDescription' });
  const schemaImage = `${config.marketplaceRootURL}${facebookImage}`;

  const [activeIndex, setActiveIndex] = useState(null);

  const handleAccordionClick = index => {
    setActiveIndex(index === activeIndex ? null : index);
  };

  // Questions and answers are en.json keys SellPage.faqN.title / SellPage.faqN.text
  const faqData = Array.from({ length: 6 }, (_, i) => `faq${i + 1}`);

  // Split FAQ into two columns for desktop
  const faqLeft = faqData.filter((_, i) => i % 2 === 0);
  const faqRight = faqData.filter((_, i) => i % 2 !== 0);

  const renderFaqCard = (faq, index) => {
    const isOpen = activeIndex === index;
    const panelId = `sell-${faq}-answer`;
    return (
      <div key={faq} className={classNames(css.faqCard, { [css.activeFAQ]: isOpen })}>
        <h3 className={css.faqTitle}>
          <button
            type="button"
            className={css.faqHeader}
            onClick={() => handleAccordionClick(index)}
            aria-expanded={isOpen}
            aria-controls={panelId}
          >
            <span className={css.faqHeading}>
              <FormattedMessage id={`SellPage.${faq}.title`} />
            </span>
            <span
              className={classNames(css.faqChevron, { [css.faqChevronOpen]: isOpen })}
              aria-hidden="true"
            >
              <IconArrowHead direction="down" size="small" />
            </span>
          </button>
        </h3>
        {isOpen && (
          <div id={panelId} className={css.faqDescription}>
            <FormattedMessage id={`SellPage.${faq}.text`} values={{ br: <br /> }} />
          </div>
        )}
      </div>
    );
  };

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        {
          url: twitterImage,
          width: 600,
          height: 314,
        },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
        image: [schemaImage],
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        {/* Section 1: Full-bleed Hero */}
        <div className={css.heroSection}>
          <div className={css.heroImageContainer}>
            <img className={css.heroImage} src={heroImage} alt={intl.formatMessage({ id: 'SellPage.heroAlt' })} />
          </div>
          <div className={css.heroOverlay}>
            <div className={css.heroContent}>
              <h1 className={css.heroHeading}>
                <FormattedMessage id="SellPage.heroHeading" />
              </h1>
              <p className={css.heroSubheading}>
                <FormattedMessage id="SellPage.heroSubheading" />
              </p>
              <NamedLink name="NewListingPage" className={css.heroCtaButton}>
                <FormattedMessage id="SellPage.listCta" />
              </NamedLink>
            </div>
          </div>
        </div>

        <div className={css.pageContent}>
          {/* Section 2: Features (3 blocks) */}
          <div className={css.featuresSection}>
            <div className={css.featureBlock}>
              <div className={css.featureImageContainer}>
                <img className={css.featureImage} src={featureImage1} alt="" />
              </div>
              <div className={css.featureContent}>
                <h2 className={css.featureHeading}>
                <FormattedMessage id="SellPage.feature1.heading" />
              </h2>
                <p className={css.featureBody}>
                <FormattedMessage id="SellPage.feature1.body" />
              </p>
              </div>
            </div>

            <div className={css.featureBlockReverse}>
              <div className={css.featureImageContainer}>
                <img className={css.featureImage} src={featureImage2} alt="" />
              </div>
              <div className={css.featureContent}>
                <h2 className={css.featureHeading}>
                <FormattedMessage id="SellPage.feature2.heading" />
              </h2>
                <p className={css.featureBody}>
                <FormattedMessage id="SellPage.feature2.body" />
              </p>
              </div>
            </div>

            <div className={css.featureBlock}>
              <div className={css.featureImageContainer}>
                <img className={css.featureImage} src={featureImage3} alt="" />
              </div>
              <div className={css.featureContent}>
                <h2 className={css.featureHeading}>
                <FormattedMessage id="SellPage.feature3.heading" />
              </h2>
                <p className={css.featureBody}>
                <FormattedMessage id="SellPage.feature3.body" />
              </p>
              </div>
            </div>

            <div className={css.featureBlockReverse}>
              <div className={css.featureImageContainer}>
                <img className={css.featureImage} src={featureImage4} alt="" />
              </div>
              <div className={css.featureContent}>
                <h2 className={css.featureHeading}>
                <FormattedMessage id="SellPage.feature4.heading" />
              </h2>
                <p className={css.featureBody}>
                <FormattedMessage id="SellPage.feature4.body" />
              </p>
              </div>
            </div>
          </div>

          {/* Section 3: Mid-page CTA */}
          <div className={css.midCtaSection}>
            <NamedLink name="NewListingPage" className={css.ctaButton}>
              <FormattedMessage id="SellPage.listCta" />
            </NamedLink>
          </div>

          {/* Section 4: How It Works */}
          <div className={css.howItWorksSection}>
            <span className={css.howItWorksEyebrow}>
                <FormattedMessage id="SellPage.howItWorksEyebrow" />
              </span>
            <h2 className={css.sectionHeading}>
                <FormattedMessage id="SellPage.howItWorksHeading" />
              </h2>
            <div className={css.howItWorksLayout}>
              <div className={css.howItWorksImageContainer}>
                <img
                  className={css.howItWorksImage}
                  src={howItWorksImage}
                  alt=""
                />
              </div>
              <div className={css.howItWorksSteps}>
                <div className={css.howItWorksStep}>
                  <div className={css.stepAccent} />
                  <div className={css.stepContent}>
                    <h3 className={css.stepHeading}>
                <FormattedMessage id="SellPage.step1.heading" />
              </h3>
                    <p className={css.stepBody}>
                <FormattedMessage id="SellPage.step1.body" />
              </p>
                  </div>
                </div>
                <div className={css.howItWorksStep}>
                  <div className={css.stepAccent} />
                  <div className={css.stepContent}>
                    <h3 className={css.stepHeading}>
                <FormattedMessage id="SellPage.step2.heading" />
              </h3>
                    <p className={css.stepBody}>
                <FormattedMessage id="SellPage.step2.body" />
              </p>
                  </div>
                </div>
                <div className={css.howItWorksStep}>
                  <div className={css.stepAccent} />
                  <div className={css.stepContent}>
                    <h3 className={css.stepHeading}>
                <FormattedMessage id="SellPage.step3.heading" />
              </h3>
                    <p className={css.stepBody}>
                <FormattedMessage id="SellPage.step3.body" />
              </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 6: Closing CTA (clean/simple) */}
          <div className={css.closingCtaSection}>
            <h2 className={css.closingCtaHeading}>
                <FormattedMessage id="SellPage.closingHeading" />
              </h2>
            <NamedLink name="NewListingPage" className={css.ctaButton}>
              <FormattedMessage id="SellPage.listCta" />
            </NamedLink>
          </div>
        </div>

        {/* Section 5: Common Questions (full-width background) */}
        <div className={css.questionsSectionOuter}>
          <div className={css.questionsSectionInner}>
            <h2 className={css.sectionHeading}>
                <FormattedMessage id="SellPage.faqHeading" />
              </h2>
            <div className={css.faqGrid}>
              <div className={css.faqColumn}>
                {faqLeft.map((faq, i) => renderFaqCard(faq, i * 2))}
              </div>
              <div className={css.faqColumn}>
                {faqRight.map((faq, i) => renderFaqCard(faq, i * 2 + 1))}
              </div>
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const { bool } = PropTypes;

SellPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  intl: intlShape.isRequired,
};

SellPageComponent.defaultProps = {};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const SellPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    null
  ),
  withViewport,
  injectIntl
)(SellPageComponent);

export default SellPage;
